import { styled } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Input from '../../../components/Input';
import HeatStrokeSvgIcon from '../../../assets/svg/general-setting/HeatStrokeSvgIcon';

const MIN_ALERT_DEBOUNCE_IN_SEC = 10;
const MIN_HEATSTROKE_DEBOUNCE_IN_SEC = 0;

const Container = styled('div')`
  border: 1px solid #e5e5e5;
  padding: 16px;
  border-radius: 8px;
  color: black;
  & > .title {
    display: flex;
    align-items: center;
  }
  & > label {
    padding-top: 15px;
    color: var(--Secondary-60, #a1a1a1);
    font-family: 'Noto Sans';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }

  & .error-message {
    color: #ff6b00;
    font-size: 0.75rem;
  }
`;

type OnChangeParams = {
  alertDebounceInSec?: number;
  heatstrokeAlertDebounceInSec?: number;
};

interface HeatstrokeAlertDebounceContentProps {
  alertDebounceInSec?: number;
  heatstrokeAlertDebounceInSec?: number;
  onChange: (params: OnChangeParams) => void;
}

const validateAlertDebounce = (
  value: number | undefined,
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value <= MIN_ALERT_DEBOUNCE_IN_SEC) {
    return `Debounce must be greater than ${MIN_ALERT_DEBOUNCE_IN_SEC}`;
  }
};

const validateHeatstrokeDebounce = (
  value: number | undefined,
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value < MIN_HEATSTROKE_DEBOUNCE_IN_SEC) {
    return `Debounce must be at least ${MIN_HEATSTROKE_DEBOUNCE_IN_SEC}`;
  }
};

const HeatstrokeAlertDebounceContent: React.FC<
  HeatstrokeAlertDebounceContentProps
> = ({ alertDebounceInSec, heatstrokeAlertDebounceInSec, onChange }) => {
  const [localAlertDebounce, setLocalAlertDebounce] =
    useState(alertDebounceInSec);
  const [localHeatstrokeDebounce, setLocalHeatstrokeDebounce] = useState(
    heatstrokeAlertDebounceInSec,
  );
  const [alertDebounceErrorMessage, setAlertDebounceErrorMessage] = useState<
    string | undefined
  >();
  const [heatstrokeDebounceErrorMessage, setHeatstrokeDebounceErrorMessage] =
    useState<string | undefined>();

  useEffect(() => {
    setLocalAlertDebounce(alertDebounceInSec);
  }, [alertDebounceInSec]);

  useEffect(() => {
    setLocalHeatstrokeDebounce(heatstrokeAlertDebounceInSec);
  }, [heatstrokeAlertDebounceInSec]);

  return (
    <Container>
      <div className="title">
        <HeatStrokeSvgIcon />
        Heatstroke Alert Notification Debounce
      </div>
      <label>{`Alert Debounce (sec) [> ${MIN_ALERT_DEBOUNCE_IN_SEC}]`}</label>
      <Input
        sx={{
          width: '100%',
          display: 'block',
          placeholderShown: 'true',
        }}
        type="number"
        min={MIN_ALERT_DEBOUNCE_IN_SEC + 1}
        value={localAlertDebounce ?? ''}
        placeholder="Leave empty to disable"
        onChange={(e) => {
          const value: number | undefined =
            e.target.value === '' ? undefined : Number(e.target.value);
          const errorMessage = validateAlertDebounce(value);
          setAlertDebounceErrorMessage(errorMessage);
          setLocalAlertDebounce(value);
          if (!errorMessage) {
            onChange({
              alertDebounceInSec: value,
              heatstrokeAlertDebounceInSec: localHeatstrokeDebounce,
            });
          }
        }}
      ></Input>
      <div className="error-message">{alertDebounceErrorMessage}</div>
      <label
        style={{ display: 'block' }}
      >{`Heatstroke Alert Debounce (sec) [min ${MIN_HEATSTROKE_DEBOUNCE_IN_SEC}]`}</label>
      <Input
        sx={{
          width: '100%',
          display: 'block',
          placeholderShown: 'true',
        }}
        type="number"
        min={MIN_HEATSTROKE_DEBOUNCE_IN_SEC}
        value={localHeatstrokeDebounce ?? ''}
        placeholder="Default: 60"
        onChange={(e) => {
          const value: number | undefined =
            e.target.value === '' ? undefined : Number(e.target.value);
          const errorMessage = validateHeatstrokeDebounce(value);
          setHeatstrokeDebounceErrorMessage(errorMessage);
          setLocalHeatstrokeDebounce(value);
          if (!errorMessage) {
            onChange({
              alertDebounceInSec: localAlertDebounce,
              heatstrokeAlertDebounceInSec: value,
            });
          }
        }}
      ></Input>
      <div className="error-message">{heatstrokeDebounceErrorMessage}</div>
    </Container>
  );
};

export default HeatstrokeAlertDebounceContent;
